import {
  AnyAction,
  // Don't remove, need to debug dispatch spam situation from streams
  // autoBatchEnhancer,
  combineReducers,
  configureStore,
  DevToolsEnhancerOptions,
} from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/dist/query';

import { ENVIRONMENT, SliceName } from '@/shared/config';
import { MIRenderMiddleware } from '@/shared/graphics/medicalImageRender/MedicalImageRender';

// Legacy for client
import accessReducer, {
  actions as accessActions,
} from '@/entities/access/model/accessSlice';
import sharingReducer from '@/entities/access/model/sharingSlice';
import albumReducer, {
  actions as albumActions,
} from '@/entities/album/model/albumSlice.ts';
import allowedToothConditionsReducer, {
  actions as allowedToothConditionsActions,
} from '@/entities/allowedToothConditions/model/allowedToothConditionsSlice';
import analyticsModelReducer, {
  actions as analyticsModelActions,
} from '@/entities/analyticsModel/model/analyticsModelSlice';
import assetsReduce, {
  actions as assetsActions,
} from '@/entities/assets/model/assetsSlice';
import {
  deleteAsset,
  setMedicalImageViewOptions,
} from '@/entities/assets/model/assetsSlice.thunks';
import authReducer, {
  actions as authActions,
} from '@/entities/auth/model/authSlice';
import billingReducer, {
  actions as billingActions,
} from '@/entities/billing/model/billingSlice';
import conditionReducer, {
  actions as conditionActions,
} from '@/entities/condition/model/conditionSlice';
import encounterReducer from '@/entities/encounter/model/encounterSlice';
import marketingReducer, {
  actions as marketingActions,
} from '@/entities/marketing/model/marketingSlice';
import modalReducer, {
  actions as modalActions,
} from '@/entities/modal/model/modalSlice';
import notificationReducer, {
  changeHasUnreadTo,
  addNotification,
  changeNotificationIsOpenTo,
  clearNotification,
} from '@/entities/notification/model/notificationSlice';
import organizationReducer, {
  actions as organizationActions,
} from '@/entities/organization/model/organizationSlice';
import { setCurrentOrganizationByID } from '@/entities/organization/model/organizationSlice.thunks';
import organizationStaffReducer, {
  actions as organizationStaffActions,
} from '@/entities/organizationStaff/model/organizationStaffSlice';
import patientReducer, {
  actions as patientActions,
} from '@/entities/patient/model/patientSlice';
import registryAssetsReducer from '@/entities/registryAssets/model/registryAssetsSlice';
import reportsReducer, {
  actions as reportsActions,
} from '@/entities/reports/model/reportSlice';
import slicesReducer, {
  actions as slicesActions,
} from '@/entities/slices/model/slicesSlice';
import studyReducer, {
  actions as studyActions,
} from '@/entities/study/model/studySlice';
import studyCountReducer, {
  actions as studyCountActions,
} from '@/entities/studyCount/model/studyCountSlice';
import templatesReducer, {
  actions as templatesActions,
} from '@/entities/templates/model/templatesSlice';
import themeReducer, {
  setTheme as themeActions,
} from '@/entities/theme/model/themeSlice';
import toolbarMIRReducer, {
  actions as toolbarMIRActions,
} from '@/entities/toolbarMIR/model/toolbarMIRSlice';
import toothReducer, {
  actions as toothActions,
} from '@/entities/tooth/model/toothSlice';
import toothLandmarksReducer, {
  actions as toothLandmarksActions,
} from '@/entities/toothLandmarks/model/toothLandmarksSlice';
import transcriptorReducer, {
  actions as transcriptorActions,
} from '@/entities/transcriptor/model/transcriptorSlice';
import userReducer, {
  actions as userActions,
} from '@/entities/user/model/userSlice';

import { highLoadReducer } from '@/features/highLoad/model/highLoadSlice';
import renderMasksReducer, {
  actions as renderMasksActions,
} from '@/features/renderMasks/model/renderMasksSlice';
import {
  toothNumberEditReducer,
  toothNumberEditActions,
} from '@/features/toothNumberEdit/model/toothNumberEditSlice';

import { errorMiddleware } from './errorMiddleware';
import { fullHeadMprSessionMiddleware } from './fullHeadMprSessionMiddleware';
import { thunkResponseMiddleware } from './middleware/thunkResponseMiddleware';
import { recordingMiddleware } from './recordingMiddleware';

const appReducer = combineReducers({
  [SliceName.access]: accessReducer,
  [SliceName.condition]: conditionReducer,
  [SliceName.marketing]: marketingReducer,
  [SliceName.modal]: modalReducer,
  [SliceName.auth]: authReducer,
  [SliceName.assets]: assetsReduce,
  [SliceName.patient]: patientReducer,
  [SliceName.notification]: notificationReducer,
  [SliceName.billing]: billingReducer,
  [SliceName.user]: userReducer,
  [SliceName.theme]: themeReducer,
  [SliceName.reports]: reportsReducer,
  [SliceName.study]: studyReducer,
  [SliceName.organization]: organizationReducer,
  [SliceName.organizationStaff]: organizationStaffReducer,
  [SliceName.studyCount]: studyCountReducer,
  [SliceName.tooth]: toothReducer,
  [SliceName.toothNumberEdit]: toothNumberEditReducer,
  [SliceName.allowedToothConditions]: allowedToothConditionsReducer,
  [SliceName.toothLandmarks]: toothLandmarksReducer,
  [SliceName.maskFilters]: renderMasksReducer,
  [SliceName.slices]: slicesReducer,
  [SliceName.toolbarMIR]: toolbarMIRReducer,
  [SliceName.album]: albumReducer,
  [SliceName.templates]: templatesReducer,
  [SliceName.transcriptor]: transcriptorReducer,
  [SliceName.encounter]: encounterReducer,
  [SliceName.analyticsModel]: analyticsModelReducer,
  [SliceName.sharing]: sharingReducer,
  [SliceName.highLoad]: highLoadReducer,
  [SliceName.registryAssets]: registryAssetsReducer,
});

const rootReducer = (
  state: ReturnType<typeof appReducer> | undefined,
  action: AnyAction,
) => {
  if (action.type === 'store/reset') {
    return appReducer(undefined, action);
  }
  return appReducer(state, action);
};

const devToolsConfig: DevToolsEnhancerOptions = {
  maxAge: 10,
};

export const store = configureStore({
  reducer: rootReducer,
  devTools: ENVIRONMENT !== 'production' && devToolsConfig,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    })
      .concat(thunkResponseMiddleware)
      .concat(errorMiddleware)
      .concat(recordingMiddleware)
      .concat(fullHeadMprSessionMiddleware)
      .concat(MIRenderMiddleware),
  // Don't remove, need to debug dispatch spam situation from streams
  // enhancers: (defaultEnchancers) =>
  //   defaultEnchancers.concat(autoBatchEnhancer({ type: 'tick' })),
});

// optional, but required for refetchOnFocus/refetchOnReconnect behaviors
// see `setupListeners` docs - takes an optional callback as the 2nd arg for customization
setupListeners(store.dispatch);

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch;

export const actions = {
  [SliceName.access]: accessActions,
  [SliceName.condition]: conditionActions,
  [SliceName.marketing]: marketingActions,
  [SliceName.modal]: modalActions,
  [SliceName.auth]: authActions,
  [SliceName.assets]: {
    ...assetsActions,
    deleteAsset,
    setMedicalImageViewOptions,
  },
  [SliceName.patient]: patientActions,
  [SliceName.notification]: {
    changeHasUnreadTo,
    addNotification,
    changeNotificationIsOpenTo,
    clearNotification,
  },
  [SliceName.billing]: billingActions,
  [SliceName.user]: userActions,
  [SliceName.theme]: { setTheme: themeActions },
  [SliceName.reports]: reportsActions,
  [SliceName.study]: studyActions,
  [SliceName.organization]: {
    ...organizationActions,
    setCurrentOrganizationID: setCurrentOrganizationByID,
  },
  [SliceName.organizationStaff]: organizationStaffActions,
  [SliceName.studyCount]: studyCountActions,
  [SliceName.tooth]: toothActions,
  [SliceName.toothNumberEdit]: toothNumberEditActions,
  [SliceName.allowedToothConditions]: allowedToothConditionsActions,
  [SliceName.toothLandmarks]: toothLandmarksActions,
  [SliceName.maskFilters]: renderMasksActions,
  [SliceName.slices]: slicesActions,
  [SliceName.toolbarMIR]: toolbarMIRActions,
  [SliceName.album]: albumActions,
  [SliceName.templates]: templatesActions,
  [SliceName.transcriptor]: transcriptorActions,
  [SliceName.analyticsModel]: analyticsModelActions,
} as const;

export type Actions = typeof actions;
